/**
 * Admin hooks for user and role management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { User, Role, RoleSummary, RolePermission, PaginatedResponse, APIKey, CreateAPIKeyInput, CreateAPIKeyResponse, APIKeyScope, UserType } from '@/types';

interface UserPermissions {
  user_id: string;
  roles: Array<{
    role: Role;
    granted_by: string;
    granted_at: string;
    is_active: boolean;
    expires_at?: string;
  }>;
  resource_permissions: Array<{
    resource_type: string;
    resource_id: string;
    permission: string;
    granted_by: string;
    granted_at: string;
    is_active: boolean;
    expires_at?: string;
  }>;
  computed_permissions: string[];
}

interface UserCreate {
  email: string;
  password?: string;
  full_name?: string;
  user_type?: UserType;
  is_active?: boolean;
  is_verified?: boolean;
  is_superuser?: boolean;
  api_key?: CreateAPIKeyInput;
}

interface UserUpdate {
  email?: string;
  full_name?: string;
  is_active?: boolean;
  is_verified?: boolean;
  is_superuser?: boolean;
}

// ============================================================================
// User CRUD Operations
// ============================================================================

// List users with pagination
export const useUsers = (params?: {
  page?: number;
  size?: number;
  search?: string;
  is_active?: boolean;
  is_verified?: boolean;
  is_superuser?: boolean;
}) => {
  const { page = 1, size = 20, search, is_active, is_verified, is_superuser } = params || {};

  return useQuery<PaginatedResponse<User>>({
    queryKey: ['admin', 'users', 'list', { page, size, search, is_active, is_verified, is_superuser }],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      if (search) searchParams.append('search', search);
      if (is_active !== undefined) searchParams.append('is_active', is_active.toString());
      if (is_verified !== undefined) searchParams.append('is_verified', is_verified.toString());
      if (is_superuser !== undefined) searchParams.append('is_superuser', is_superuser.toString());

      const response = await api.get(`/admin/users?${searchParams.toString()}`);
      return response.data;
    },
  });
};

// Get single user details
export const useUser = (userId: string) => {
  return useQuery<User>({
    queryKey: ['admin', 'users', userId],
    queryFn: async () => {
      const response = await api.get(`/admin/users/${userId}`);
      return response.data;
    },
    enabled: !!userId,
  });
};

// Create user
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userData: UserCreate) => {
      const response = await api.post('/admin/users', userData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
};

// Create API-only user (creates user + API key in one request)
// Uses the same endpoint as regular user creation, but with user_type='api_only'
// and API key fields at the top level (not nested)
export const useCreateApiOnlyUser = () => {
  const queryClient = useQueryClient();

  return useMutation<
    { user: User; api_key: string; api_key_id: string; api_key_prefix: string; api_key_expires_at: string | null; warning: string },
    Error,
    UserCreate
  >({
    mutationFn: async (userData: UserCreate) => {
      // Flatten the api_key object to top-level fields as expected by the backend
      const payload: Record<string, unknown> = {
        email: userData.email,
        full_name: userData.full_name,
        user_type: userData.user_type || 'api_only',
        is_active: userData.is_active ?? true,
        is_verified: userData.is_verified ?? true,
        is_superuser: userData.is_superuser ?? false,
      };

      // Flatten nested api_key object to top-level fields
      if (userData.api_key) {
        payload.api_key_name = userData.api_key.name;
        payload.api_key_description = userData.api_key.description;
        payload.api_key_scopes = userData.api_key.scopes;
        payload.api_key_expires_in_days = userData.api_key.expires_in_days;
      }

      const response = await api.post('/admin/users', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
};

// Update user
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UserUpdate }) => {
      const response = await api.patch(`/admin/users/${userId}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'list'] });
    },
  });
};

// Delete user (soft delete)
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.delete(`/admin/users/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
};

// Resend the magic-link setup link for a pending (unverified) human user.
// Regenerates the token, re-emails the link, and invalidates any prior link.
export const useResendSetupLink = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.post(`/admin/users/${userId}/resend-setup-link`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
};

// ============================================================================
// RBAC Operations
// ============================================================================

// Get user permissions and roles
export const useUserPermissions = (userId: string) => {
  return useQuery<UserPermissions>({
    queryKey: ['admin', 'users', userId, 'permissions'],
    queryFn: async () => {
      const response = await api.get(`/admin/users/${userId}/permissions`);
      return response.data;
    },
    enabled: !!userId,
  });
};

// List available roles
export const useAvailableRoles = () => {
  return useQuery<{ roles: Role[]; total: number }>({
    queryKey: ['admin', 'roles', 'available'],
    queryFn: async () => {
      const response = await api.get('/admin/users/roles/available');
      return response.data;
    },
  });
};

// Assign role to user
export const useAssignRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string }) => {
      const response = await api.post(`/admin/users/${userId}/roles`, {
        role_id: roleId,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
};

// Revoke role from user
export const useRevokeRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, roleId }: { userId: string; roleId: string }) => {
      const response = await api.delete(`/admin/users/${userId}/roles`, {
        data: { role_id: roleId },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
};

// Grant resource permission
export const useGrantResourcePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      resourceType,
      resourceId,
      permission,
    }: {
      userId: string;
      resourceType: string;
      resourceId: string;
      permission: string;
    }) => {
      const response = await api.post(`/admin/users/${userId}/resource-permissions`, {
        resource_type: resourceType,
        resource_id: resourceId,
        permission,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
};

// Revoke resource permission
export const useRevokeResourcePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      resourceType,
      resourceId,
      permission,
    }: {
      userId: string;
      resourceType: string;
      resourceId: string;
      permission: string;
    }) => {
      const response = await api.delete(`/admin/users/${userId}/resource-permissions`, {
        data: {
          resource_type: resourceType,
          resource_id: resourceId,
          permission,
        },
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId] });
    },
  });
};

// Check user permission
export const useCheckPermission = () => {
  return useMutation({
    mutationFn: async ({
      userId,
      permission,
      resourceType,
      resourceId,
    }: {
      userId: string;
      permission: string;
      resourceType?: string;
      resourceId?: string;
    }) => {
      const response = await api.post(`/admin/users/${userId}/check-permission`, {
        permission,
        resource_type: resourceType,
        resource_id: resourceId,
      });
      return response.data;
    },
  });
};

// Invalidate user cache
export const useInvalidateUserCache = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const response = await api.post(`/admin/users/${userId}/invalidate-cache`);
      return response.data;
    },
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', userId] });
    },
  });
};

// ============================================================================
// Role CRUD Operations
// ============================================================================

interface RoleCreate {
  name: string;
  description: string;
  parent_role_id?: string;
  permission_ids?: string[];
}

interface RoleUpdate {
  name?: string;
  description?: string;
  parent_role_id?: string;
  is_active?: boolean;
}

/**
 * `GET /admin/roles/{id}`. Both permission arrays are `list[RolePermissionInfo]`
 * on the backend (`RoleDetailResponse`), so both are `RolePermission[]` here.
 * They were two hand-copied inline shapes until #1473 — a third and fourth copy
 * of the same fields, which is exactly how they came to drift out of sync with
 * the wire's nullability.
 */
interface RoleDetail extends Role {
  parent_role_name?: string;
  permissions: RolePermission[];
  inherited_permissions: RolePermission[];
  child_roles: string[];
  user_count: number;
}

// List roles with pagination
/**
 * `GET /admin/roles`. The two boolean filters are the route's own **inclusive**
 * flags (`role_management.py`'s `list_roles`), not equality filters: they widen
 * the result set rather than selecting a value. They were named `is_active` /
 * `is_system_role` here until #1473 — names `list_roles` never declared, which
 * FastAPI dropped silently, so both filters no-opped.
 */
export const useRoles = (params?: {
  page?: number;
  size?: number;
  search?: string;
  include_inactive?: boolean;
  include_system_roles?: boolean;
  parent_role_id?: string;
}) => {
  const { page = 1, size = 20, search, include_inactive, include_system_roles, parent_role_id } = params || {};

  return useQuery<PaginatedResponse<RoleSummary>>({
    queryKey: ['admin', 'roles', 'list', { page, size, search, include_inactive, include_system_roles, parent_role_id }],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
      });

      // `!== undefined`, not truthiness: `include_inactive=false` and
      // `include_system_roles=false` are meaningful explicit requests and must
      // reach the route rather than falling back to its default.
      if (search) searchParams.append('search', search);
      if (include_inactive !== undefined) searchParams.append('include_inactive', include_inactive.toString());
      if (include_system_roles !== undefined) searchParams.append('include_system_roles', include_system_roles.toString());
      if (parent_role_id) searchParams.append('parent_role_id', parent_role_id);

      const response = await api.get(`/admin/roles?${searchParams.toString()}`);
      return response.data;
    },
  });
};

// Get single role with details
export const useRole = (roleId: string) => {
  return useQuery<RoleDetail>({
    queryKey: ['admin', 'roles', roleId],
    queryFn: async () => {
      const response = await api.get(`/admin/roles/${roleId}`);
      return response.data;
    },
    enabled: !!roleId,
  });
};

// Create role
export const useCreateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleData: RoleCreate) => {
      const response = await api.post('/admin/roles', roleData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });
};

// Update role
export const useUpdateRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, data }: { roleId: string; data: RoleUpdate }) => {
      const response = await api.put(`/admin/roles/${roleId}`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles', variables.roleId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles', 'list'] });
    },
  });
};

// Delete role (soft delete)
export const useDeleteRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId: string) => {
      const response = await api.delete(`/admin/roles/${roleId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
  });
};

// Get role permissions
//
// `includeInherited` defaults to `true` (the endpoint's own default) so
// existing callers keep their current meaning. Pass `false` to fetch only
// the permissions granted directly on the role (no walk up `parent_role_id`).
// The flag is part of the query key so the two variants cache separately.
export const useRolePermissions = (roleId: string, includeInherited: boolean = true) => {
  return useQuery<RolePermission[]>({
    queryKey: ['admin', 'roles', roleId, 'permissions', { includeInherited }],
    queryFn: async () => {
      const response = await api.get(`/admin/roles/${roleId}/permissions`, {
        params: { include_inherited: includeInherited },
      });
      return response.data;
    },
    enabled: !!roleId,
  });
};

// Assign permission to role
export const useAssignRolePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, permissionId }: { roleId: string; permissionId: string }) => {
      const response = await api.post(`/admin/roles/${roleId}/permissions`, {
        permission_id: permissionId,
      });
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles', variables.roleId] });
    },
  });
};

// Remove permission from role
export const useRemoveRolePermission = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, permissionId }: { roleId: string; permissionId: string }) => {
      const response = await api.delete(`/admin/roles/${roleId}/permissions/${permissionId}`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles', variables.roleId] });
    },
  });
};

// ============================================================================
// API Key Operations
// ============================================================================

// Get API keys for a user
export const useUserApiKeys = (userId: string) => {
  return useQuery<APIKey[]>({
    queryKey: ['admin', 'users', userId, 'api-keys'],
    queryFn: async () => {
      const response = await api.get(`/admin/users/${userId}/api-keys`);
      return response.data;
    },
    enabled: !!userId,
  });
};

// Get available API key scopes
export const useApiKeyScopes = () => {
  return useQuery<APIKeyScope[]>({
    queryKey: ['admin', 'api-keys', 'scopes'],
    queryFn: async () => {
      const response = await api.get('/admin/api-keys/scopes');
      return response.data;
    },
  });
};

// Create API key for a user
export const useCreateUserApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateAPIKeyResponse,
    Error,
    { userId: string; data: CreateAPIKeyInput }
  >({
    mutationFn: async ({ userId, data }) => {
      // Backend expects query parameters, not POST body
      const params = new URLSearchParams();
      params.append('key_name', data.name);
      if (data.description) {
        params.append('key_description', data.description);
      }
      if (data.expires_in_days !== undefined) {
        params.append('expires_in_days', data.expires_in_days.toString());
      }
      // Add scopes as multiple parameters
      data.scopes.forEach(scope => {
        params.append('scopes', scope);
      });

      const response = await api.post(`/admin/users/${userId}/api-keys?${params.toString()}`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId, 'api-keys'] });
    },
  });
};

// Revoke API key
export const useRevokeUserApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { userId: string; keyId: string }
  >({
    mutationFn: async ({ userId, keyId }) => {
      const response = await api.delete(`/admin/users/${userId}/api-keys/${keyId}`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId, 'api-keys'] });
    },
  });
};

// Rotate API key
export const useRotateUserApiKey = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CreateAPIKeyResponse,
    Error,
    { userId: string; keyId: string }
  >({
    mutationFn: async ({ userId, keyId }) => {
      const response = await api.post(`/admin/users/${userId}/api-keys/${keyId}/rotate`);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users', variables.userId, 'api-keys'] });
    },
  });
};
